import React, { useEffect, useState } from 'react'
import { useI18n } from '../i18n/index.jsx'
import DashboardLayout from '../components/DashboardLayout'
import ReactGA from 'react-ga4'

export default function Extension() {
    const { t } = useI18n()

    // Track page view
    useEffect(() => {
      ReactGA.send({ hitType: "pageview", page: "/extension", title: "Extension" });
    }, []);

    // Minimal, non-invasive extension detection state
    // null = checking, true = installed, false = not installed
    const [extInstalled, setExtInstalled] = useState(null)
    const [tokenValue, setTokenValue] = useState('')
    const [showToken, setShowToken] = useState(false)
    const [copyStatus, setCopyStatus] = useState('')

    useEffect(() => {
        let settled = false

        // quick heuristic: some extensions expose helpers on window
        try {
            if (typeof window !== 'undefined' && (window.showShortcutConfirmation || window.showCenteredLoadingAnimation || window.__Hiperyon_extension_present)) {
                setExtInstalled(true)
                settled = true
                return
            }
        } catch (e) { /* ignore */ }

        function handleMessage(e) {
            try {
                const d = e && e.data
                if (!d) return
                if (d && (d.type === 'MY_EXTENSION_HERE' || d.type === 'Hiperyon_EXTENSION_PONG' || d.__Hiperyon_extension === true)) {
                    settled = true
                    setExtInstalled(true)
                }
            } catch (err) { /* ignore */ }
        }

        window.addEventListener('message', handleMessage)
        try { window.postMessage({ type: 'CHECK_MY_EXTENSION' }, '*') } catch (e) {}

        const timeout = setTimeout(() => {
            if (!settled) setExtInstalled(false)
            window.removeEventListener('message', handleMessage)
        }, 1200)

        // try load a token for the copy field (non-invasive)
        try {
            const keys = ['access_token', 'chatgpt_token', 'jwt_token', 'jwtToken']
            for (const k of keys) {
                const v = localStorage.getItem(k)
                if (v) { setTokenValue(v); break }
            }
        } catch(e) {}

        return () => {
            clearTimeout(timeout)
            window.removeEventListener('message', handleMessage)
        }
    }, [])

    const copyToken = async () => {
        if (!tokenValue) { setCopyStatus(t('extension.step2.token.none')); setTimeout(()=>setCopyStatus(''),2000); return }
        try {
            await navigator.clipboard.writeText(tokenValue)
            setCopyStatus(t('extension.step2.token.copied'))
        } catch (e) { setCopyStatus(t('extension.step2.token.error')) }
        setTimeout(()=>setCopyStatus(''),2000)
    }

    const steps = [
        {
            number: '01',
            title: t('extension.step1.title'),
            description: t('extension.step1.description')
        },
        {
            number: '02',
            title: t('extension.step2.title'),
            description: t('extension.step2.description')
        },
        {
            number: '03',
            title: t('extension.step3.title'),
            description: t('extension.step3.description'),
            highlight: true
        },
        {
            number: '04',
            title: t('extension.step4.title'),
            description: t('extension.step4.description')
        }
    ]

    return (
        <DashboardLayout>
            <div className="flex-1 p-4 md:p-8">
                {/* Header */}
                <div className="mb-6 md:mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 md:p-3 rounded-xl md:rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border border-emerald-500/30">
                            <svg className="h-5 w-5 md:h-6 md:w-6 text-emerald-400" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2L2 7V10C2 16 6 20.9 12 22C18 20.9 22 16 22 10V7L12 2M10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z"/>
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-xl md:text-3xl font-bold text-white">{t('extension.title')}</h1>
                            <p className="text-sm md:text-base text-slate-400">{t('extension.subtitle')}</p>
                        </div>
                    </div>
                </div>

                {/* Status Card */}
                <div className="mb-6 md:mb-8 rounded-xl md:rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/2 p-4 md:p-6 backdrop-blur-sm">
                    <div className="flex flex-col md:flex-row items-start justify-between mb-4 md:mb-6 gap-3">
                        <div className="flex-1">
                            <h3 className="text-lg md:text-xl font-semibold text-white mb-2 flex flex-wrap items-center gap-2 md:gap-3">
                                <span>{t('extension.status.title')}</span>
                                <span className={`inline-flex items-center gap-2 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium border ${extInstalled === null ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' : extInstalled ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-red-500/20 text-red-300 border-red-500/30'}`}>
                                    <div className={`h-2 w-2 rounded-full ${extInstalled === null ? 'bg-yellow-400 animate-pulse' : extInstalled ? 'bg-emerald-400' : 'bg-red-400 animate-pulse'}`}></div>
                                    {extInstalled === null ? t('extension.status.checking') : extInstalled ? t('extension.status.installed') : t('extension.status.notInstalled')}
                                </span>
                            </h3>
                            <p className="text-sm md:text-base text-slate-400">{extInstalled ? t('extension.status.description.installed') : t('extension.status.description.notInstalled')}</p>
                        </div>
                        <div className="text-left md:text-right">
                            <div className={`text-xl md:text-2xl font-bold ${extInstalled ? 'text-emerald-400' : 'text-red-400'}`}>{extInstalled ? '100%' : extInstalled === null ? '…' : '0%'}</div>
                            <div className="text-xs text-slate-500">{t('extension.status.configuration')}</div>
                        </div>
                    </div>

                    {/* Progress Steps */}
                    <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
                        <div className={`flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-lg md:rounded-xl ${extInstalled ? 'bg-emerald-900/20 border border-emerald-800/30' : 'bg-slate-800/50 border border-slate-700/50'}`}>
                            <div className="flex-shrink-0">
                                <div className={`h-7 w-7 md:h-8 md:w-8 rounded-full flex items-center justify-center ${extInstalled ? 'bg-emerald-500/20 border-2 border-emerald-500/40' : 'bg-red-500/20 border-2 border-red-500/40'}`}>
                                    <svg className={`h-3 w-3 md:h-4 md:w-4 ${extInstalled ? 'text-emerald-400' : 'text-red-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        {extInstalled ? (
                                            <path d="M5 12l4 4L19 6" />
                                        ) : (
                                            <>
                                                <circle cx="12" cy="12" r="10"/>
                                                <line x1="15" y1="9" x2="9" y2="15"/>
                                                <line x1="9" y1="9" x2="15" y2="15"/>
                                            </>
                                        )}
                                    </svg>
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-sm md:text-base text-white font-medium">{t('extension.status.chrome')}</h4>
                                <p className="text-xs md:text-sm text-slate-400 truncate">{extInstalled ? t('extension.status.chrome.detected') : t('extension.status.chrome.notDetected')}</p>
                            </div>
                            <div className={`${extInstalled ? 'text-emerald-400' : 'text-red-400'} text-xs md:text-sm font-medium whitespace-nowrap`}>{extInstalled ? t('extension.status.connected') : t('extension.status.waiting')}</div>
                        </div>

                        {/* Authentification and Synchronisation cards removed per request */}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                        {extInstalled ? (
                            <button disabled className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600/20 px-4 md:px-6 py-2.5 md:py-3 text-sm md:text-base font-semibold text-emerald-300 border border-emerald-500/30">
                                <svg className="h-4 w-4 md:h-5 md:w-5 text-emerald-300" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M5 12l4 4L19 6" />
                                </svg>
                                <span className="truncate">{t('extension.btn.installed')}</span>
                            </button>
                        ) : (
                            <a href="https://chromewebstore.google.com/detail/llacahflaikdpllhjolamccddjiceelb?utm_source=item-share-cb" target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-400 px-4 md:px-6 py-2.5 md:py-3 text-sm md:text-base font-semibold text-slate-950 hover:from-emerald-400 hover:to-emerald-300 transition">
                                <svg className="h-4 w-4 md:h-5 md:w-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2L2 7V10C2 16 6 20.9 12 22C18 20.9 22 16 22 10V7L12 2M10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z"/>
                                </svg>
                                <span className="truncate">{t('extension.btn.install')}</span>
                            </a>
                        )}
                        <a href="/doc-extension" className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 md:px-6 py-2.5 md:py-3 text-sm md:text-base font-semibold text-white hover:bg-white/20 transition backdrop-blur-sm">
                            <svg className="h-4 w-4 md:h-5 md:w-5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"/>
                            </svg>
                            <span className="truncate">{t('extension.btn.documentation')}</span>
                        </a>
                    </div>
                </div>

                {/* How it Works */}
                <div className="mb-8 md:mb-12">
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">{t('extension.howItWorks')}</h2>
                    <div className="space-y-4 md:space-y-6">
                        {steps.map((step, index) => (
                            <div key={index} className={`flex flex-col sm:flex-row gap-3 md:gap-6 items-start p-4 md:p-6 rounded-xl md:rounded-2xl border backdrop-blur-sm ${
                                step.highlight 
                                    ? 'border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 ring-1 ring-emerald-500/20' 
                                    : 'border-white/10 bg-gradient-to-br from-white/5 to-white/2'
                            }`}>
                                <div className="flex-shrink-0">
                                    <div className={`flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full text-base md:text-lg font-bold ${
                                        step.highlight
                                            ? 'bg-gradient-to-r from-emerald-400 to-emerald-300 text-slate-950'
                                            : 'bg-gradient-to-r from-emerald-500 to-emerald-400 text-slate-950'
                                    }`}>
                                        {step.number}
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <h3 className={`text-lg md:text-xl font-semibold mb-2 ${
                                        step.highlight ? 'text-emerald-300' : 'text-white'
                                    }`}>
                                        {step.title}
                                        {step.highlight && (
                                            <span className="ml-2 md:ml-3 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 text-xs md:text-sm font-medium border border-emerald-500/30">
                                                <kbd className="px-1.5 md:px-2 py-0.5 md:py-1 bg-slate-800 rounded text-xs">Ctrl</kbd>
                                                <span>+</span>
                                                <kbd className="px-1.5 md:px-2 py-0.5 md:py-1 bg-slate-800 rounded text-xs">Q</kbd>
                                            </span>
                                        )}
                                    </h3>
                                    <p className="text-sm md:text-base text-slate-400 leading-relaxed">
                                        {step.description}
                                    </p>

                                    {/* Amélioration visuelle : carte simple pour lier l'extension */}
                                    {step.number === '02' && (
                                        <div className="mt-3 md:mt-4 p-3 md:p-4 rounded-lg md:rounded-xl bg-gradient-to-br from-white/2 to-white/3 border border-white/6">
                                            <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4">
                                                <div className="flex-shrink-0">
                                                    <div className="h-9 w-9 md:h-10 md:w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                                                        <svg className="h-4 w-4 md:h-5 md:w-5 text-emerald-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                            <path d="M12 2v6" strokeLinecap="round" strokeLinejoin="round" />
                                                            <rect x="4" y="8" width="16" height="12" rx="2" />
                                                            <path d="M8 12h8" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                </div>

                                                <div className="flex-1 w-full">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div>
                                                            <div className="text-sm font-semibold text-white">{t('extension.step2.link.title')}</div>
                                                            <div className="text-xs text-slate-400">{t('extension.step2.link.description')}</div>
                                                        </div>
                                                    </div>

                                                    <div className="mt-3 flex flex-col sm:flex-row gap-2 md:gap-3">
                                                        <input
                                                            type={showToken ? 'text' : 'password'}
                                                            value={tokenValue}
                                                            onChange={e => setTokenValue(e.target.value)}
                                                            placeholder={t('extension.step2.token.placeholder')}
                                                            className="flex-1 px-3 py-2 text-sm md:text-base rounded-md bg-slate-900 border border-white/10 text-white placeholder:text-slate-500"
                                                        />

                                                        <div className="flex gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowToken(s => !s)}
                                                                className="inline-flex items-center justify-center px-3 py-2 rounded-md bg-slate-800 border border-white/10 text-slate-200"
                                                                title={showToken ? t('extension.step2.token.hide') : t('extension.step2.token.show')}
                                                                aria-pressed={showToken}
                                                            >
                                                                {showToken ? (
                                                                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path d="M17.94 17.94A10.6 10.6 0 0 1 12 19c-5 0-9.27-3.11-11-7 1.03-2.04 2.69-3.8 4.73-4.93" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        <path d="M1 1l22 22" strokeLinecap="round" strokeLinejoin="round"/>
                                                                    </svg>
                                                                ) : (
                                                                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        <circle cx="12" cy="12" r="3"/>
                                                                    </svg>
                                                                )}
                                                            </button>

                                                            <button
                                                                onClick={copyToken}
                                                                className="inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-md bg-emerald-500 text-slate-900 text-sm md:text-base font-semibold whitespace-nowrap"
                                                            >
                                                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M16 21H8a2 2 0 0 1-2-2V7" strokeLinecap="round" strokeLinejoin="round" />
                                                                    <rect x="8" y="3" width="12" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                                <span>{t('extension.step2.token.copy')}</span>
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {copyStatus && (
                                                        <div className="mt-2 text-xs md:text-sm text-slate-300">{copyStatus}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Tutorial Section */}
                <div className="mb-6 md:mb-8 rounded-xl md:rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 p-4 md:p-6 backdrop-blur-sm">
                    <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4">
                        <div className="flex-shrink-0">
                            <div className="p-2 md:p-3 rounded-lg md:rounded-xl bg-emerald-500/20">
                                <svg className="h-5 w-5 md:h-6 md:w-6 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <path d="M9,11 l3,3 l8,-8"/>
                                </svg>
                            </div>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg md:text-xl font-semibold text-emerald-300 mb-2">
                                {t('extension.tutorial.title')}
                            </h3>
                            <p className="text-sm md:text-base text-slate-300 mb-3 md:mb-4">
                                {t('extension.tutorial.description')}
                            </p>
                            <a href="/doc-extension" className="inline-flex items-center gap-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 px-3 md:px-4 py-2 text-sm md:text-base font-semibold text-emerald-300 hover:bg-emerald-500/30 transition">
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"/>
                                </svg>
                                <span>{t('extension.tutorial.btn')}</span>
                            </a>
                        </div>
                    </div>
                </div>

                {/* Call to Action */}
                <div className="rounded-2xl md:rounded-3xl border border-white/10 bg-gradient-to-br from-white/10 to-white/5 p-6 md:p-8 backdrop-blur-sm text-center">
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4">
                        {t('extension.cta.title')}
                    </h2>
                    <p className="text-base md:text-lg text-slate-300 mb-4 md:mb-6">
                        {t('extension.cta.description')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                        {extInstalled ? (
                            <button disabled className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600/20 px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-semibold text-emerald-300 border border-emerald-500/30">
                                <svg className="h-4 w-4 md:h-5 md:w-5 text-emerald-300" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M5 12l4 4L19 6" />
                                </svg>
                                <span>{t('extension.btn.installed')}</span>
                            </button>
                        ) : (
                            <a href="https://chromewebstore.google.com/detail/llacahflaikdpllhjolamccddjiceelb?utm_source=item-share-cb" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-400 px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-semibold text-slate-950 hover:from-emerald-400 hover:to-emerald-300 transition">
                                <svg className="h-4 w-4 md:h-5 md:w-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2L2 7V10C2 16 6 20.9 12 22C18 20.9 22 16 22 10V7L12 2M10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z"/>
                                </svg>
                                <span>{t('extension.cta.installNow')}</span>
                            </a>
                        )}
                        <button className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/10 px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-semibold text-white hover:bg-white/20 transition backdrop-blur-sm">
                            <svg className="h-4 w-4 md:h-5 md:w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                                <polyline points="10,17 15,12 10,7"/>
                                <line x1="15" y1="12" x2="3" y2="12"/>
                            </svg>
                            <span>{t('extension.cta.examples')}</span>
                        </button>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    )
}
